/**
 * Pure logic for the day-by-day itinerary view.
 *
 * Everything here answers a question the collapsed day card has to answer
 * without a DOM: "what happens this day?", "should this day start open?", and
 * "which rows are things that HAPPEN versus context that merely CONTINUES?".
 *
 * Kept out of the component on purpose — the summary line is the whole product
 * of the collapsed state, and a summary line you cannot assert is a summary
 * line nobody can trust.
 */
import type {
  FlightSegment,
  ItemSegment,
  ItineraryDay,
  ItineraryItemType,
  ItinerarySegment,
  OngoingSpan,
} from "@/lib/types";

/* ── Narrowing ─────────────────────────────────────────────────────────── */

export function isFlight(seg: ItinerarySegment): seg is FlightSegment {
  return seg.kind === "flight";
}

export function isItem(seg: ItinerarySegment): seg is ItemSegment {
  return seg.kind === "item";
}

export function flightsOf(day: ItineraryDay): FlightSegment[] {
  return day.segments.filter(isFlight);
}

export function itemsOf(day: ItineraryDay): ItemSegment[] {
  return day.segments.filter(isItem);
}

/**
 * True when this item does not merely happen today — it opens a span that runs
 * past today. Its later days arrive on those days as `OngoingSpan`s.
 */
export function isSpanStart(item: ItemSegment): boolean {
  return !!item.end_date && item.end_date > item.day_date;
}

/* ── Dates ─────────────────────────────────────────────────────────────── */

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export interface DayHeading {
  /** "Saturday" */
  weekday: string;
  /** "Sat" — the 375px header. */
  weekdayShort: string;
  /** "Sep 12" */
  date: string;
  /** "Saturday, Sep 12" — the print heading and the aria label. */
  full: string;
}

/**
 * "2026-09-12" -> Saturday, Sep 12.
 *
 * Parsed field-by-field and read back through the UTC getters. `new Date("2026-09-12")`
 * is parsed as midnight UTC, so every traveller west of Greenwich would have
 * seen the PREVIOUS day's name against the correct date — a whole itinerary
 * off by one, silently, only for them.
 */
export function dayHeading(iso: string): DayHeading {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return { weekday: iso, weekdayShort: iso, date: iso, full: iso };
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  const weekday = WEEKDAYS[dt.getUTCDay()] ?? "";
  const date = (MONTHS[mo - 1] ?? "") + " " + d;
  return {
    weekday,
    weekdayShort: weekday.slice(0, 3),
    date,
    full: weekday + ", " + date,
  };
}

/* ── The collapsed summary line ────────────────────────────────────────── */

/** Singular/plural nouns for the "3 activities" tail of the summary. */
const COUNT_NOUN: Record<ItineraryItemType, [string, string]> = {
  event: ["event", "events"],
  lodging: ["stay", "stays"],
  dining: ["meal", "meals"],
  activity: ["activity", "activities"],
  transfer: ["transfer", "transfers"],
  note: ["note", "notes"],
  cruise: ["cruise", "cruises"],
  port: ["port of call", "ports of call"],
};

/** Counted in this order so the day reads by importance, not by enum order. */
const COUNT_ORDER: ItineraryItemType[] = [
  "event",
  "port",
  "activity",
  "dining",
  "transfer",
  "note",
];

export interface DaySummary {
  /** Ordered clauses. Join with " · " for display; assert on this in tests. */
  parts: string[];
  /** The joined line. "Nothing planned" when the day carries nothing. */
  text: string;
  /** True when there is nothing at all — no segment and no ongoing span. */
  empty: boolean;
}

/**
 * One line that answers "what happens this day?" at a glance.
 *
 * Order is deliberate and matches how a traveller scans: the flight first
 * (it is the thing with a gate time), then the frame the day sits inside (a
 * cruise), then where you sleep, then named things worth naming, then counts.
 *
 * Named-not-counted: an anchor (the reason for the trip) and anything opening a
 * multi-day span. "1 event" is a worse sentence than "Sarah's wedding".
 */
export function daySummary(day: ItineraryDay): DaySummary {
  const parts: string[] = [];
  const flights = flightsOf(day);
  const items = itemsOf(day);

  if (flights.length > 0) {
    parts.push(
      "Fly " + flights.map((f) => f.origin + " → " + f.destination).join(", "),
    );
  }

  // The frame the day sits inside — a cruise, a festival. Lodging is handled
  // below under "where you sleep" so it lands in the traveller's order.
  for (const span of day.ongoing) {
    if (span.item_type === "lodging") continue;
    parts.push(span.label + " · " + span.title);
  }

  // Where you sleep. A lodging that STARTS today is a check-in (an action);
  // a lodging that merely covers today is the quiet "still here" line.
  const lodgingStart = items.find((i) => i.item_type === "lodging");
  const lodgingOngoing = day.ongoing.find((o) => o.item_type === "lodging");
  if (lodgingStart) {
    parts.push("Check in · " + lodgingStart.title);
  } else if (lodgingOngoing) {
    parts.push(lodgingOngoing.label + " · " + lodgingOngoing.title);
  }

  // Everything else: name the ones worth naming, count the rest.
  const rest = items.filter((i) => i.item_type !== "lodging");
  const named = rest.filter((i) => i.is_anchor || isSpanStart(i));
  for (const item of named) parts.push(item.title);

  const counted = rest.filter((i) => !named.includes(i));
  for (const type of COUNT_ORDER) {
    const n = counted.filter((i) => i.item_type === type).length;
    if (n === 0) continue;
    const [one, many] = COUNT_NOUN[type];
    parts.push(n + " " + (n === 1 ? one : many));
  }

  const empty = parts.length === 0;
  return { parts, text: empty ? "Nothing planned" : parts.join(" · "), empty };
}

/* ── Collapse defaults ─────────────────────────────────────────────────── */

export interface ExpandOptions {
  /** Days in the whole trip — a short trip is not worth collapsing. */
  totalDays: number;
  /** The printed document reveals everything, always. */
  printMode?: boolean;
}

/**
 * Should this day start open?
 *
 * The rule, in the order it is applied:
 *  1. Print reveals everything — a document that hides content is not a document.
 *  2. A trip of 3 days or fewer opens whole. Collapsing it is pure friction:
 *     the reader would tap every card immediately.
 *  3. An empty day stays shut. There is nothing behind it but the invitation to
 *     add something, and repeating that invitation down a column of free days is
 *     the exact noise the owner asked us to remove.
 *  4. A day with a flight opens. Flights carry times you must not miss and a
 *     confirmation you may need at a counter; that is worth the vertical space.
 *  5. A day with the anchor opens. It is the reason the trip exists.
 *  6. Everything else stays shut behind its summary line.
 */
export function defaultExpanded(
  day: ItineraryDay,
  opts: ExpandOptions,
): boolean {
  if (opts.printMode) return true;
  if (opts.totalDays <= 3) return true;
  if (day.empty) return false;
  if (flightsOf(day).length > 0) return true;
  if (itemsOf(day).some((i) => i.is_anchor)) return true;
  return false;
}

/** The set of dates that start expanded, ready for component state. */
export function initialExpandedDates(
  days: ItineraryDay[],
  printMode = false,
): string[] {
  const opts: ExpandOptions = { totalDays: days.length, printMode };
  return days.filter((d) => defaultExpanded(d, opts)).map((d) => d.date);
}

/* ── Timeline rows ─────────────────────────────────────────────────────── */

export type TimelineRow =
  | { kind: "ongoing"; isContext: true; span: OngoingSpan }
  | { kind: "segment"; isContext: false; segment: ItinerarySegment };

/**
 * The expanded day, in reading order: context first, then what happens.
 *
 * `ongoing` spans are always `isContext: true` and are always built from
 * `day.ongoing` — never from `day.segments`. That is the guard against the one
 * bug this view can most easily grow: rendering "still at the hotel" as though
 * you checked in again this morning.
 */
export function timelineRows(day: ItineraryDay): TimelineRow[] {
  const rows: TimelineRow[] = day.ongoing.map((span) => ({
    kind: "ongoing" as const,
    isContext: true as const,
    span,
  }));
  for (const segment of day.segments) {
    rows.push({ kind: "segment", isContext: false, segment });
  }
  return rows;
}

/** "Night 2 of 3 · Hotel Captain Cook" — the quiet context row's text. */
export function ongoingRowLabel(span: OngoingSpan): string {
  return span.label + " · " + span.title;
}

/** "Day 4" — position within the trip, 1-based. */
export function dayOrdinal(index: number): string {
  return "Day " + (index + 1);
}
